import useWidth from '@components/Hooks/useWidth';
import { Stack } from '@mui/material';
import { ReactNode, cloneElement, isValidElement } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { FilterTestIds } from '../../../../utils/testIds/Filter.testIds';
import Button from '../Button/Button';
import { SearchFieldProps } from './components/SearchField/SearchField';
import { useFilterBarStyles } from './filterBar.styles';
import { useFilterBar } from './hooks/useFilterBar';

interface FilterBarProps {
  fixedFilters?: ReactNode;
  chips?: ReactNode;
  hasActiveSearch?: boolean;
  onClearAll?: () => void;
  searchField?: ReactNode;
}

function FilterBar({
  fixedFilters,
  chips,
  hasActiveSearch,
  onClearAll,
  searchField,
}: FilterBarProps) {
  const { rootRef, handleSurfaceClick, hasActiveValues, hasControls, hasSearchField } =
    useFilterBar({
      fixedFilters,
      chips,
      hasActiveSearch,
      searchField,
    });
  const { smallWidth } = useWidth();
  const { classes } = useFilterBarStyles({
    hasActiveFilter: hasActiveValues,
    hasSearchField,
  });
  const { translate } = useTranslation();

  const isEmpty = !hasSearchField && !hasControls && !hasActiveValues;

  if (isEmpty) {
    return null;
  }

  const searchNode = searchField ? (
    <div className={classes.searchWrapper}>
      {isValidElement<Partial<SearchFieldProps>>(searchField)
        ? cloneElement(searchField, { hasOtherContent: hasControls })
        : searchField}
    </div>
  ) : null;

  const controlsNode = hasControls ? (
    <Stack
      direction="row"
      className={classes.controls}
      onClick={(event) => event.stopPropagation()}
    >
      {fixedFilters}
      {chips}
    </Stack>
  ) : null;

  return (
    <Stack ref={rootRef} direction="row" className={classes.root} onClick={handleSurfaceClick}>
      <Stack direction="row" className={classes.content}>
        {smallWidth ? (
          <>
            {searchNode}
            {controlsNode}
          </>
        ) : (
          <>
            {controlsNode}
            {searchNode}
          </>
        )}
      </Stack>
      {onClearAll && hasActiveValues && (
        <Button
          variant="text"
          color="secondary"
          size="medium"
          testId={FilterTestIds.CLEAR_ALL_FILTERS_BUTTON}
          className={classes.clearAll}
          onClick={(event) => {
            event.stopPropagation();
            onClearAll();
          }}
        >
          {translate(LangEnum.GLOBAL_CLEAR)}
        </Button>
      )}
    </Stack>
  );
}

export default FilterBar;
